import { createFileRoute, useParams } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useStore } from '../../../store';
import layoutStyles from '../../../components/ProjectsView.module.css';
import sharedStyles from '../../../components/ProjectSecurity.module.css';
import styles from './CustomDomains.module.css';
import { waitForAck } from '../../../utils/waitForAck';
import { dispatch, waitForReady } from '../../../messaging';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import type { CustomHostname } from '@interlucent/organization-protocol';

export const Route = createFileRoute('/projects/$projectId/domains')({
  component: ProjectDomainsTab,
});

type StatusBadgeVariant = 'pending' | 'active' | 'failed';

function StatusBadge({ status }: { status: StatusBadgeVariant }) {
  const variants: Record<StatusBadgeVariant, { label: string; className: string }> = {
    pending: { label: 'Pending', className: styles.statusBadgePending },
    active: { label: 'Active', className: styles.statusBadgeActive },
    failed: { label: 'Failed', className: styles.statusBadgeFailed },
  };

  const variant = variants[status] || variants.pending;

  return (
    <span className={`${styles.statusBadge} ${variant.className}`}>
      {variant.label}
    </span>
  );
}

function ProjectDomainsTab() {
  const { projectId } = useParams({ from: '/projects/$projectId/domains' });
  const project = useStore(s => s.projects[projectId]);
  const allCustomHostnames = useStore(s => s.customHostnames);
  const isConnected = useStore(s => s.connectionState === 'connected');

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newHostname, setNewHostname] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<CustomHostname | null>(null);
  const [refreshingIds, setRefreshingIds] = useState<Set<string>>(new Set());

  // Filter hostnames for this project
  const projectHostnames = useMemo(() => {
    return Object.values(allCustomHostnames).filter(h => h.projectId === projectId);
  }, [allCustomHostnames, projectId]);

  if (!project) return null;

  // Check if custom domains feature is enabled for this project
  // Note: customDomainsEnabled may not be in types yet - using type assertion
  const customDomainsEnabled = (project as any).customDomainsEnabled;

  if (!customDomainsEnabled) {
    return (
      <div className={sharedStyles.wrapper}>
        <div className={sharedStyles.section}>
          <div className={sharedStyles.sectionHeader}>
            <div>
              <div className={sharedStyles.sectionTitle}>Custom Domains</div>
              <p className={sharedStyles.sectionDescription}>
                Custom domains are not enabled for this project. Contact your administrator
                to enable this feature.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleAddHostname = async () => {
    if (!newHostname.trim()) {
      setStatusMessage('Please enter a hostname.');
      return;
    }

    // Basic hostname validation
    const hostnameRegex = /^(?!-)[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;
    if (!hostnameRegex.test(newHostname.trim())) {
      setStatusMessage('Please enter a valid hostname (e.g., api.example.com).');
      return;
    }

    setIsSubmitting(true);
    setStatusMessage('');

    try {
      await waitForReady();
      const corr = uuidv4();
      dispatch('customHostname.create', { projectId, hostname: newHostname.trim() }, corr);

      const response = await waitForAck<{ id: string; cnameTarget: string; status: string }>(corr);

      setNewHostname('');
      setIsAddDialogOpen(false);
      setStatusMessage(`Custom hostname added. Configure your DNS with a CNAME record pointing to ${response.cnameTarget}`);
    } catch (error: any) {
      setStatusMessage(error?.message || 'Failed to add custom hostname');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteHostname = async () => {
    if (!deleteTarget) return;

    setIsSubmitting(true);

    try {
      await waitForReady();
      const corr = uuidv4();
      dispatch('customHostname.delete', { id: deleteTarget.id }, corr);
      await waitForAck(corr);

      setDeleteTarget(null);
      setStatusMessage('Custom hostname deleted.');
    } catch (error: any) {
      setStatusMessage(error?.message || 'Failed to delete custom hostname');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefreshHostname = async (hostname: CustomHostname) => {
    setRefreshingIds(prev => new Set([...prev, hostname.id]));

    try {
      await waitForReady();
      const corr = uuidv4();
      dispatch('customHostname.refresh', { id: hostname.id }, corr);
      await waitForAck<{ status: string }>(corr);
      setStatusMessage('Status refreshed.');
    } catch (error: any) {
      setStatusMessage(error?.message || 'Failed to refresh status');
    } finally {
      setRefreshingIds(prev => {
        const next = new Set(prev);
        next.delete(hostname.id);
        return next;
      });
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setStatusMessage('Copied to clipboard.');
    } catch {
      setStatusMessage('Failed to copy to clipboard.');
    }
  };

  return (
    <div className={sharedStyles.wrapper}>
      <div className={sharedStyles.section}>
        <div className={sharedStyles.sectionHeader}>
          <div>
            <div className={sharedStyles.sectionTitle}>Custom Domains</div>
            <p className={sharedStyles.sectionDescription}>
              Configure custom domains to access your project's applications through
              your own branded URLs.
            </p>
          </div>
          <button
            type="button"
            className={sharedStyles.button}
            onClick={() => setIsAddDialogOpen(true)}
          >
            Add Domain
          </button>
        </div>

        {projectHostnames.length === 0 ? (
          <div className={sharedStyles.note}>
            No custom domains configured. Click "Add Domain" to get started.
          </div>
        ) : (
          <div className={styles.domainList}>
            {projectHostnames.map(hostname => (
              <div key={hostname.id} className={styles.domainCard}>
                <div className={styles.domainCardHeader}>
                  <div className={styles.domainCardInfo}>
                    <h3 className={styles.domainHostname}>
                      {hostname.hostname}
                    </h3>
                    <StatusBadge status={hostname.status as StatusBadgeVariant} />
                  </div>
                  <div className={styles.cardActions}>
                    <button
                      type="button"
                      className={`${sharedStyles.button} ${sharedStyles.buttonSecondary}`}
                      onClick={() => handleRefreshHostname(hostname)}
                      disabled={refreshingIds.has(hostname.id)}
                    >
                      {refreshingIds.has(hostname.id) ? 'Refreshing...' : 'Refresh'}
                    </button>
                    <button
                      type="button"
                      className={`${sharedStyles.button} ${sharedStyles.buttonSecondary} ${styles.buttonDanger}`}
                      onClick={() => setDeleteTarget(hostname)}
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {hostname.status === 'pending' && (
                  <div className={styles.instructionsBox}>
                    <h4 className={styles.instructionsTitle}>Setup Instructions</h4>
                    <ol className={styles.instructionsList}>
                      <li>Add a CNAME record for your domain:</li>
                    </ol>
                    <div className={styles.cnameRecord}>
                      <span className={styles.cnameSource}>{hostname.hostname}</span>
                      <span className={styles.cnameArrow}>&rarr;</span>
                      <span className={styles.cnameTarget}>{hostname.cnameTarget}</span>
                      <button
                        type="button"
                        className={styles.cnameCopyButton}
                        onClick={() => copyToClipboard(hostname.cnameTarget)}
                        title="Copy CNAME target"
                      >
                        Copy
                      </button>
                    </div>

                    {hostname.dcvDelegationTarget && (
                      <>
                        <ol start={2} className={styles.instructionsList}>
                          <li>Add a CNAME record for SSL certificate validation (DCV):</li>
                        </ol>
                        <div className={styles.cnameRecord}>
                          <span className={styles.cnameSource}>_acme-challenge.{hostname.hostname}</span>
                          <span className={styles.cnameArrow}>&rarr;</span>
                          <span className={styles.cnameTarget}>{hostname.dcvDelegationTarget}</span>
                          <button
                            type="button"
                            className={styles.cnameCopyButton}
                            onClick={() => copyToClipboard(hostname.dcvDelegationTarget!)}
                            title="Copy DCV target"
                          >
                            Copy
                          </button>
                        </div>
                        <ol start={3} className={styles.instructionsList}>
                          <li>Wait for DNS propagation (up to 24 hours)</li>
                          <li>SSL certificate will be provisioned automatically</li>
                        </ol>
                      </>
                    )}

                    {!hostname.dcvDelegationTarget && (
                      <ol start={2} className={styles.instructionsList}>
                        <li>Wait for DNS propagation (up to 24 hours)</li>
                        <li>SSL certificate will be provisioned automatically</li>
                      </ol>
                    )}
                  </div>
                )}

                {hostname.status === 'active' && (
                  <div className={styles.successMessage}>
                    DNS configured correctly. Your domain is active.
                  </div>
                )}

                {hostname.status === 'failed' && (
                  <div className={styles.errorBox}>
                    <h4 className={styles.errorTitle}>
                      Configuration Error
                    </h4>
                    {hostname.verificationErrors && hostname.verificationErrors.length > 0 ? (
                      <ul className={styles.errorList}>
                        {hostname.verificationErrors.map((error, i) => (
                          <li key={i}>{error}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className={styles.errorText}>
                        SSL validation failed. Please verify your DNS configuration and try refreshing.
                      </p>
                    )}
                  </div>
                )}

                {hostname.sslStatus && hostname.status !== 'active' && (
                  <div className={styles.sslStatus}>
                    SSL Status: {hostname.sslStatus}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {statusMessage && (
        <div className={layoutStyles.errorMessage}>
          <span className={layoutStyles.errorText}>{statusMessage}</span>
          <button
            type="button"
            onClick={() => setStatusMessage('')}
            className={layoutStyles.errorClose}
            aria-label="Dismiss status"
          >
            &times;
          </button>
        </div>
      )}

      {/* Add Domain Dialog */}
      <ConfirmDialog
        isOpen={isAddDialogOpen}
        title="Add Custom Domain"
        message={
          <div className={styles.dialogContent}>
            <p className={styles.dialogText}>
              Enter the hostname you want to use for this project.
            </p>
            <input
              type="text"
              value={newHostname}
              onChange={e => setNewHostname(e.target.value)}
              placeholder="api.example.com"
              className={styles.dialogInput}
              autoFocus
            />
            <p className={styles.dialogHint}>
              After adding, you'll need to configure a CNAME record in your DNS provider.
            </p>
          </div>
        }
        confirmText={isSubmitting ? 'Adding...' : 'Add Domain'}
        cancelText="Cancel"
        onConfirm={handleAddHostname}
        onCancel={() => {
          setIsAddDialogOpen(false);
          setNewHostname('');
        }}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete Custom Domain"
        message={`Are you sure you want to delete ${deleteTarget?.hostname}? This action cannot be undone.`}
        confirmText={isSubmitting ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
        onConfirm={handleDeleteHostname}
        onCancel={() => setDeleteTarget(null)}
        requireConfirmation={true}
        confirmationPhrase="delete"
      />
    </div>
  );
}
